import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../interfaces/api/user';
import { authentication } from '../services/tokens.service';
import { fetchUser, fetchUserPermissions } from '../services/users.service';
import { UserFilterParams } from '../../api/users';

interface AuthenticationState {
	loggedIn: boolean;
	isLoading: boolean;
	user: User | undefined;
	permissions: string[];
}

const initialState = {
	loggedIn: false,
	isLoading: false,
	user: undefined,
	permissions: [],
} as AuthenticationState;

export const authenticateUserThunk = createAsyncThunk<
	User,
	{ email: string; password: string; withToken: boolean; params: UserFilterParams }
>('authentication/authenticate', async (data, { rejectWithValue }) => {
	try {
		await authentication(data.email, data.password, data.withToken);
		return await fetchUser(data.params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const fetchUserThunk = createAsyncThunk<User, { params: UserFilterParams }>(
	'authentication/fetchUser',
	async (data, { rejectWithValue }) => {
		try {
			return await fetchUser(data.params);
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchUserPermissionsThunk = createAsyncThunk<string[], void>(
	'authentication/fetchUserPermissions',
	async (_undefined, { rejectWithValue }) => {
		try {
			return await fetchUserPermissions();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const logout = createAsyncThunk<void, void>(
	'authentication/logout',
	async (_undefined, { rejectWithValue }) => {
		try {
			await logout();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

const authenticationSlice = createSlice({
	name: 'authentication',
	initialState,
	reducers: {
		setLoggedInStatus: (state, action: PayloadAction<boolean>) => {
			state.loggedIn = action.payload;
		},
		resetAuthentication: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(authenticateUserThunk.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(authenticateUserThunk.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(authenticateUserThunk.rejected, (state) => {
				state.user = undefined;
				state.permissions = [];
				state.isLoading = false;
			})

			.addCase(fetchUserThunk.fulfilled, (state, action) => {
				state.user = action.payload;
				state.isLoading = false;
			})
			.addCase(fetchUserThunk.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchUserThunk.rejected, (state) => {
				state.user = undefined;
				state.isLoading = false;
			})

			.addCase(fetchUserPermissionsThunk.fulfilled, (state, action) => {
				state.permissions = action.payload;
				state.isLoading = false;
			})
			.addCase(fetchUserPermissionsThunk.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchUserPermissionsThunk.rejected, (state) => {
				state.permissions = [];
				state.isLoading = false;
			})

			.addCase(logout.fulfilled, (state) => {
				state.user = undefined;
				state.permissions = [];
				state.loggedIn = false;
			});
	},
});

export const { setLoggedInStatus, resetAuthentication } = authenticationSlice.actions;

export default authenticationSlice;
